import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { bomService } from "../services/svc_boms";
import { queryKeys } from "../lib/queryKeys";
import { ListRequest, ListRequestSchemaV2 } from "../types/common/commonTypes";

export const useTSQBOMRevList = (
  params: ListRequest,
  queryEnabled: boolean,
) => {
  return useQuery({
    queryKey: queryKeys.bomrev.list(params),
    queryFn: () => bomService.listBomRev(params),
    placeholderData: keepPreviousData,
    enabled: queryEnabled,
  });
};

export const useTSQBOMRevListView = (
  params: ListRequestSchemaV2,
  source: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: queryKeys.bomrev.listv2(params, source),
    queryFn: () => bomService.listBomRevV2(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQBOMRev = (id: string) => {
  return useQuery({
    queryKey: queryKeys.bomrev.detail(id),
    queryFn: () => bomService.getBomRevById(id),
    enabled: !!id,
  });
};

export const useTSMCreateBOMRev = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bomService.createBomRev,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bomrev.lists() });
    },
    onError: (error) => {
      console.error("Failed to create BOMRev:", error);
    },
  });
};

export const useTSMUpdateBOMRev = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      bomService.updateBomRev(id, data),
    onSuccess: (updateBOMRev) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bomrev.lists() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.bomrev.detail(updateBOMRev.id),
      });
    },
    onError: (error) => {
      console.error("Failed to update BOMRev:", error);
    },
  });
};

export const useTSMDeactivateBOMRev = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bomService.deactivateBomRev,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bomrev.lists() });
    },
    onError: (error) => {
      console.error("Failed to deactivate BOMRev:", error);
    },
  });
};

export const useTSMReactivateBOMRev = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bomService.reactivateBomRev,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bomrev.lists() });
    },
    onError: (error) => {
      console.error("Failed to reactivate BOMRev:", error);
    },
  });
};

export const useTSQBOMCompList = (
  params: ListRequestSchemaV2,
  source: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.bomcomp.list(params, source),
    queryFn: () => bomService.listBomComp(params),
    placeholderData: keepPreviousData,
    enabled: enabled,
  });
};

export const useTSQBomCompsByRev = (revId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: queryKeys.bomcomp.list(null, revId),
    queryFn: () => bomService.getBomCompsByRev(revId),
    enabled: enabled,
  });
};
